import { useEffect, useState } from "react";
import { Radar, Plus, Loader2, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useSavedTopics } from "@/hooks/useSavedTopics";
import EmptyStateShell from "./EmptyStateShell";
import { toast } from "sonner";

const CURATED_TOPIC_EXAMPLES = [
  "Shopify Plus migration tips",
  "TikTok Shop ad strategies",
  "Klaviyo email flow benchmarks",
  "Amazon DSP for DTC brands",
];

/**
 * Empty state for the Tracked Topics tab.
 *
 * Hybrid suggestion strategy:
 *  - Pull the signed-in user's last ~10 search queries (deduped, valid).
 *  - Mix with curated example topics so the list always feels rich.
 *  - Each chip is a one-click "Track this" action.
 */
export default function EmptyTrackedTopics() {
  const { user } = useAuth();
  const { saveTopic, savedQueries } = useSavedTopics();
  const [recentQueries, setRecentQueries] = useState<string[]>([]);
  const [savingQuery, setSavingQuery] = useState<string | null>(null);
  const [tracked, setTracked] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from("search_logs")
      .select("query, normalized_query, created_at")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(20)
      .then(({ data }) => {
        const seen = new Set<string>();
        const out: string[] = [];
        for (const row of data ?? []) {
          const q = (row.query || "").trim();
          if (!q || q.length < 4 || q.length > 140) continue;
          const key = (row.normalized_query || q).toLowerCase().trim();
          if (seen.has(key)) continue;
          seen.add(key);
          out.push(q);
          if (out.length >= 3) break;
        }
        setRecentQueries(out);
      });
  }, [user?.id]);

  // Build the hybrid blend: 2-3 recents + fill with curated, deduped against
  // already-saved topics. Cap at 5 chips total to stay scannable.
  const lowerSaved = savedQueries; // already lowercased Set from hook
  const blend: { text: string; source: "yours" | "trending" }[] = [];
  const seenLower = new Set<string>();
  for (const q of recentQueries.slice(0, 3)) {
    const lower = q.toLowerCase().trim();
    if (lowerSaved.has(lower) || seenLower.has(lower)) continue;
    seenLower.add(lower);
    blend.push({ text: q, source: "yours" });
  }
  for (const q of CURATED_TOPIC_EXAMPLES) {
    if (blend.length >= 5) break;
    const lower = q.toLowerCase().trim();
    if (lowerSaved.has(lower) || seenLower.has(lower)) continue;
    seenLower.add(lower);
    blend.push({ text: q, source: "trending" });
  }

  const handleTrack = async (query: string) => {
    setSavingQuery(query);
    const ok = await saveTopic(query);
    setSavingQuery(null);
    if (ok) {
      setTracked((prev) => new Set(prev).add(query.toLowerCase().trim()));
      toast.success(`Tracking "${query}"`);
    } else {
      toast.error("Already tracking that topic");
    }
  };

  return (
    <EmptyStateShell
      icon={Radar}
      emoji="🛰️"
      headline="Set it. We'll hunt for you."
      blurb="Track a topic and we'll scan every new episode, surfacing matches in your library and (optionally) emailing you when something juicy drops. Like a Google Alert — for podcasts."
    >
      <p className="font-mono text-[10px] sm:text-[11px] uppercase tracking-wider text-muted-foreground font-bold mb-2">
        ⚡ Track one with a single tap
      </p>
      <div className="flex flex-wrap gap-1.5 sm:gap-2">
        {blend.map(({ text, source }) => {
          const lower = text.toLowerCase().trim();
          const isTracked = tracked.has(lower);
          const isSaving = savingQuery === text;
          return (
            <button
              key={text}
              onClick={() => handleTrack(text)}
              disabled={isTracked || isSaving}
              className={`group flex items-center gap-1 sm:gap-1.5 font-mono text-[10px] sm:text-xs px-2.5 sm:px-3 py-1.5 sm:py-2 retro-border transition-colors min-h-[36px] ${
                isTracked
                  ? "bg-green-500/20 cursor-default"
                  : "bg-background hover:bg-retro-amber/25 active:translate-y-px"
              }`}
              title={isTracked ? "Tracked" : `Track "${text}"`}
            >
              {isSaving ? (
                <Loader2 className="w-3 h-3 animate-spin flex-shrink-0" />
              ) : isTracked ? (
                <Check className="w-3 h-3 flex-shrink-0" />
              ) : (
                <Plus className="w-3 h-3 flex-shrink-0 opacity-60 group-hover:opacity-100" />
              )}
              <span className="truncate max-w-[150px] sm:max-w-[240px]">{text}</span>
              {source === "yours" && !isTracked && (
                <span className="text-[8px] sm:text-[9px] text-primary font-bold uppercase ml-0.5">you</span>
              )}
            </button>
          );
        })}
      </div>
      <p className="font-mono text-[9px] sm:text-[10px] text-muted-foreground mt-3 italic leading-snug">
        💡 Or run a search and tap "Track Topic" on the results page.
      </p>
    </EmptyStateShell>
  );
}
